import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePlanDetail } from '../hooks/usePlans';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import UnifiedMap from '../components/UnifiedMap';
import { planActivitiesToMarkers } from '../utils/mapMarkers';
import { HeroImage } from '../components/common';
import { BottomNav } from '../components/navigation';
import { CommunityActivityThumb } from '../components/plans/CommunityActivityThumb';
import './PlansV2.css';
import './DayDetailV2.css';

const DayDetailV2: React.FC = () => {
  const { slug, dayNum: dayNumStr } = useParams<{ slug: string; dayNum: string }>();
  const navigate = useNavigate();
  const { plan, isLoading, error } = usePlanDetail(slug);
  // Whether the hero is the paper fallback (no image, or it failed to load),
  // as HeroImage reports it. Bare, the overlay paints no scrim and its
  // controls and title go ink (decision 25, r1123 finding 1).
  const [heroBare, setHeroBare] = React.useState(false);

  // Day numbers are offsets from the trip's start date, so a plan whose items
  // predate it legitimately has a day 0 or -1 — and the snapshot stores them.
  // Existence is decided by `plan.days` below, not by the sign.
  const dayNum = /^-?\d+$/.test(dayNumStr || '') ? parseInt(dayNumStr!, 10) : NaN;

  // Plan loading
  if (isLoading) {
    return (
      <div className="pv2-page">
        <div className="pv2-loading">Loading day...</div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  // Plan not found
  if (error || !plan) {
    return (
      <div className="pv2-page">
        <div className="pv2-not-found">
          <p className="pv2-not-found-title">Plan not found</p>
          <p className="pv2-not-found-body">This plan may have been removed or the link is incorrect.</p>
          <Link to="/explore" className="pv2-back-link">Back to Explore</Link>
        </div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  // Day validation
  const day = plan.days.find(d => d.day === dayNum);
  if (!day || isNaN(dayNum)) {
    return (
      <div className="pv2-page">
        <div className="pv2-not-found">
          <p className="pv2-not-found-title">Day not found</p>
          <p className="pv2-not-found-body">This day doesn't exist in the itinerary.</p>
          <Link to={`/plans/p/${slug}`} className="pv2-back-link">Back to plan</Link>
        </div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  const prevDay = plan.days.find(d => d.day === dayNum - 1);
  const nextDay = plan.days.find(d => d.day === dayNum + 1);
  const markers = planActivitiesToMarkers(day.activities, dayNum);
  const dayImage = plan.day_images?.[String(dayNum)];

  return (
    <div className="pv2-page ddv2">
      {/* Hero */}
      <div className={`ddv2-hero${heroBare ? ' ddv2-hero--bare' : ''}`}>
        {/* This day's own photo when it has one, else the plan's hero — and
            credit whichever one is actually on screen. */}
        <HeroImage
          src={dayImage?.url || plan.hero_image_url}
          fallbackKey={day.label}
          size="hero"
          attribution={
            dayImage?.url
              ? (dayImage.photographer
                ? { photographer: dayImage.photographer, link: dayImage.unsplash_link }
                : undefined)
              // Falling back to the plan's hero credits the PLAN's photograph.
              // Crediting the day image here would name a photographer whose
              // picture is not on screen; crediting nobody would drop a pool
              // hero's credit entirely. The src decides, so the credit follows it.
              : (plan.hero_attribution
                ? {
                  photographer: plan.hero_attribution.photographer,
                  link: plan.hero_attribution.unsplash_link,
                }
                : undefined)
          }
          onBareChange={setHeroBare}
          showLabel={false}
        />
        <div className={`ddv2-hero-overlay${heroBare ? ' ddv2-hero-overlay--bare' : ''}`}>
          <div className="ddv2-hero-top">
            <button className="ddv2-back-btn" onClick={() => navigate(`/plans/p/${slug}`)}>
              <ArrowLeft size={16} /> Back
            </button>
            <div className="ddv2-hero-nav">
              {prevDay && (
                <button className="ddv2-nav-btn" onClick={() => navigate(`/plans/p/${slug}/day/${prevDay.day}`)}>
                  <ChevronLeft size={14} />
                </button>
              )}
              {nextDay && (
                <button className="ddv2-nav-btn" onClick={() => navigate(`/plans/p/${slug}/day/${nextDay.day}`)}>
                  <ChevronRight size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="ddv2-hero-bottom">
            <span className="ddv2-day-label">Day {day.day}</span>
            <h1 className="ddv2-day-title">{day.label}</h1>
          </div>
        </div>
      </div>

      {/* Day map */}
      {markers.length > 0 && (
        <div className="ddv2-map">
          <UnifiedMap markers={markers} />
        </div>
      )}

      {/* Day content */}
      <div className="ddv2-content">
        {/* Activities */}
        {day.activities.map((act, i) => (
          <div key={i} className="ddv2-activity">
            <div className="ddv2-activity-timeline">
              <span className="ddv2-activity-num">{i + 1}</span>
              {i < day.activities.length - 1 && <div className="ddv2-activity-connector" />}
            </div>
            <CommunityActivityThumb
              placeId={act.place_id}
              pool={act.pool}
              name={act.name}
            />
            <div className="ddv2-activity-card">
              <span className="ddv2-activity-name">{act.name}</span>
              {act.time && <span className="ddv2-activity-time">{act.time}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Prev/Next navigation */}
      <div className="ddv2-footer-nav">
        {prevDay ? (
          <button className="ddv2-footer-btn" onClick={() => navigate(`/plans/p/${slug}/day/${prevDay.day}`)}>
            <ChevronLeft size={14} /> Day {prevDay.day}
          </button>
        ) : <div />}
        {nextDay ? (
          <button className="ddv2-footer-btn" onClick={() => navigate(`/plans/p/${slug}/day/${nextDay.day}`)}>
            Day {nextDay.day} <ChevronRight size={14} />
          </button>
        ) : <div />}
      </div>

      <BottomNav activeItem="explore" />
    </div>
  );
};

export default DayDetailV2;
